import { PersonDTO } from "../dto/user/PersonDTO.ts";
import IUserRepository from "../interfaces/IUserRepository.ts";
import { IPersonState, IUserStep } from "../dto/user/IPersonState.ts";
import { OnboardingStep } from "@/store/onboardingStore.ts";
import { getMockSession, updateMockSession } from "./AuthRepositoryMock.ts";
import { ClientLegalType } from "../dto/client/ClientDTO.ts";
import { IChallengeResponse } from "../dto/user/IChallengeResponse.ts";
import { ISolutionResponse } from "../dto/user/ISolutionResponse.ts";
import { useStorage } from "@vueuse/core";
import { Ref } from "vue";
import { IRepositoryError } from "../dto/errors/IRepositoryError.ts";
import { IAuthorization } from "../dto/auth/Authorization.ts";

type IChallengeStorage = {
  type: string,
  code: string,
  solution: string,
  time: number
}

export default class UserRepositoryMock implements IUserRepository {
  createPersonProfile(): Promise<PersonDTO> {
    return Promise.resolve({} as PersonDTO);
  }

  getState(): Promise<IPersonState> {
    const userState = getMockSession();
    console.log("UserRepositoryMock.getState() %s ", "userState", userState);

    if (!userState || !userState.user) {
      return Promise.resolve({} as IPersonState);
    }

    function getStep(id: string, validated: boolean = false): IUserStep {
      return {
        key: id,
        isValid: validated,
        isMandatory: true
      };
    }

    const state: IPersonState = {
      person: userState.user as any,
      steps: [],
      currentStep: OnboardingStep.EMAIL_PASSWORD,
      restrictionReason: null
    };

    if ((userState.user as any).clients.length > 0) {
      const user = userState.user as any;
      const client = (userState.user as any).clients[0];

      if (client.legalType === ClientLegalType.natural) {
        state.steps = [];
        state.currentStep = OnboardingStep.DONE;
      } else {
        if (!user.profile) {
          state.steps = [
            getStep(OnboardingStep.APPLICATION_PERSONAL),
            getStep(OnboardingStep.APPLICATION_COMPANY),
            getStep(OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE),
            getStep(OnboardingStep.APPLICATION_COMPANY_OWNERS)
          ];
          state.currentStep = OnboardingStep.APPLICATION_PERSONAL;
        } else if (!client.profile) {
          state.steps = [
            getStep(OnboardingStep.APPLICATION_PERSONAL, true),
            getStep(OnboardingStep.APPLICATION_COMPANY),
            getStep(OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE),
            getStep(OnboardingStep.APPLICATION_COMPANY_OWNERS)
          ];
          state.currentStep = OnboardingStep.APPLICATION_COMPANY;
        } else if (!client.profile) {
          state.steps = [
            getStep(OnboardingStep.APPLICATION_PERSONAL, true),
            getStep(OnboardingStep.APPLICATION_COMPANY, true),
            getStep(OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE),
            getStep(OnboardingStep.APPLICATION_COMPANY_OWNERS)
          ];
          state.currentStep = OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE;
        } else if (!client.profile) {
          state.steps = [
            getStep(OnboardingStep.APPLICATION_PERSONAL, true),
            getStep(OnboardingStep.APPLICATION_COMPANY, true),
            getStep(OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE, true),
            getStep(OnboardingStep.APPLICATION_COMPANY_OWNERS)
          ];
          state.currentStep = OnboardingStep.APPLICATION_COMPANY_OWNERS;
        } else {
          state.steps = [
            getStep(OnboardingStep.APPLICATION_PERSONAL, true),
            getStep(OnboardingStep.APPLICATION_COMPANY, true),
            getStep(OnboardingStep.APPLICATION_COMPANY_QUESTIONNAIRE, true),
            getStep(OnboardingStep.APPLICATION_COMPANY_OWNERS, true)
          ];
          state.currentStep = OnboardingStep.DONE;
        }
      }

      return Promise.resolve(state);
    }

    if (!userState.email?.verified) {
      state.steps = [
        getStep(OnboardingStep.EMAIL_VERIFY),
        getStep(OnboardingStep.PHONE),
        getStep(OnboardingStep.PHONE_VERIFY)
      ];
      state.currentStep = OnboardingStep.EMAIL_VERIFY;
    } else if (!userState.phone) {
      state.steps = [
        getStep(OnboardingStep.EMAIL_VERIFY, true),
        getStep(OnboardingStep.PHONE),
        getStep(OnboardingStep.PHONE_VERIFY)
      ];
      state.currentStep = OnboardingStep.PHONE;
    } else if (!userState.phone.verified) {
      state.steps = [
        getStep(OnboardingStep.EMAIL_VERIFY, true),
        getStep(OnboardingStep.PHONE, true),
        getStep(OnboardingStep.PHONE_VERIFY)
      ];
      state.currentStep = OnboardingStep.PHONE_VERIFY;
    } else {
      state.steps = [
        getStep(OnboardingStep.EMAIL_VERIFY, true),
        getStep(OnboardingStep.PHONE, true),
        getStep(OnboardingStep.PHONE_VERIFY, true)
      ];
      state.currentStep = OnboardingStep.DONE;
    }

    return Promise.resolve(state);
  }

  updatePersonProfile(): Promise<PersonDTO> {
    return Promise.resolve({} as PersonDTO);
  }

  async getChallenge(challengeType: string, challengeCode: string | null): Promise<IChallengeResponse | IRepositoryError> {
    const _challengeCode = challengeCode ? challengeCode : (Math.random() * 10e6).toFixed();
    const _solution = (challengeType === "validate_email" || challengeType === "email") ? "123456" : "000000";
    const storage: Ref<IChallengeStorage> = useStorage("userStore-getChallenge", {
      type: challengeType,
      code: _challengeCode,
      solution: _solution,
      time: Date.now()
    });

    if (storage.value.type !== challengeType) {
      storage.value = {
        type: challengeType,
        code: _challengeCode,
        solution: _solution,
        time: Date.now()
      };
    }

    await new Promise((r) => setTimeout(() => r(true), 2000));

    let timeout = (Date.now() - (storage.value.time + 60));
    timeout = timeout < 0 ? 0 : timeout;

    return Promise.resolve({
      type: storage.value.type,
      code: storage.value.code,
      timeout: timeout
    } as IChallengeResponse);
  }

  async verifySolution(challengeCode: string, solutionCode: string): Promise<ISolutionResponse | IRepositoryError> {
    const storage: Ref<IChallengeStorage> = useStorage("userStore-getChallenge", {}) as Ref<IChallengeStorage>;

    await new Promise((r) => setTimeout(() => r(true), 2000));

    const state: IAuthorization | null = getMockSession();
    let verified = challengeCode === storage.value.code && storage.value.solution === solutionCode;
    console.log("challengeCode (%s) === storage.value.code (%s) && storage.value.solution (%s) === solutionCode (%s)", challengeCode, storage.value.code, storage.value.solution, solutionCode);
    if (verified) {
      if (state != null) {
        const fld = (storage.value.type === "validate_phone" || storage.value.type === "phone") ? "phone" : "email";
        if (state.hasOwnProperty(fld)) {
          Object.assign(state[fld] ?? {}, {
            verified: true,
            verifiedAt: new Date()
          });

          updateMockSession(state);
        } else {
          verified = false;
        }
      }
    }

    return {
      verified: verified,
      challenge: {
        type: storage.value.type,
        code: storage.value.code,
        timeout: 10000
      }
    } as ISolutionResponse;
  }

  async addPhone(countryCode: string, phone: string): Promise<IChallengeResponse | IRepositoryError> {
    const state = getMockSession();
    updateMockSession(Object.assign(state ?? {}, {
      phone: {
        id: "keyId",
        phoneShort: phone,
        phoneCountry: countryCode,
        phoneFull: countryCode + "" + phone,
        verified: false,
        verifiedAt: null,
        createdAt: new Date
      }
    }));

    return this.getChallenge("phone", null);
  }

  async createPersonalProfile(profile: object): Promise<any> {
    console.log("UserRepositoryMock.createPersonalProfile", profile);
    const state = getMockSession();
    if (state && state.user) {
      (state.user as any).profile = {
        ...profile,
        status: null, // Status will be set to IN_REVIEW after verification
        createdAt: new Date()
      } as any;
      updateMockSession(state);
    }
    await new Promise((r) => setTimeout(() => r(true), 1000));
    return Promise.resolve({ success: true });
  }

  async createPersonalQuestionnaire(questionnaire: object): Promise<any> {
    console.log("UserRepositoryMock.createPersonalQuestionnaire", questionnaire);
    await new Promise((r) => setTimeout(() => r(true), 1000));
    return Promise.resolve({ success: true });
  }

  async getVerifyToken(): Promise<any> {
    console.log("UserRepositoryMock.getVerifyToken");
    await new Promise((r) => setTimeout(() => r(true), 500));
    return Promise.resolve({ token: "mock-verify-token-" + Date.now() });
  }

  async createBusinessProfile(_profile: object): Promise<any> {
    await new Promise((r) => setTimeout(() => r(true), 1000));
    return Promise.resolve({ success: true });
  }

  async createBusinessOwners(_owners: object): Promise<any> {
    await new Promise((r) => setTimeout(() => r(true), 1000));
    return Promise.resolve({ success: true });
  }

  async updateReviewStatus(): Promise<any> {
    console.log("UserRepositoryMock.updateReviewStatus");
    const state = getMockSession();
    if (state && state.user?.profile) {
      // Update profile status to IN_REVIEW after verification
      (state.user.profile as any).status = "IN_REVIEW";
      updateMockSession(state);
      console.log("UserRepositoryMock.updateReviewStatus - Updated profile status to IN_REVIEW");
    }
    await new Promise((r) => setTimeout(() => r(true), 500));
    return Promise.resolve({ success: true, status: "IN_REVIEW" });
  }
}
